import { logError, logWarn } from '@ardoq/logging';
import { LayoutRequestContext, WorkerError, WorkerErrorLevel } from './types';
import { fromSerializedWorkerError } from './workerErrorSerialization';
import type {
  LateLayoutWorkerStart,
  LayoutWorkerStartFailure,
  StartFailureCause,
} from './layoutWorkerConnection';

export const isWorkerError = (data: unknown): data is WorkerError =>
  Boolean(data) && typeof data === 'object' && 'errorLevel' in data!;

const logWorkerError = ({
  error,
  message,
  extraData,
  errorLevel,
}: WorkerError): void => {
  const log = errorLevel === WorkerErrorLevel.ERROR ? logError : logWarn;
  log(fromSerializedWorkerError(error), message, extraData);
};

const describeCause = (
  cause: StartFailureCause
): {
  error: Error;
  message: string;
  extraData: Record<string, unknown> & { fingerprint: string[] };
} => {
  switch (cause.reason) {
    case 'timeout':
      return {
        error: Error('Layout worker never signalled that it was initialized'),
        message: 'Layout worker never signalled that it was initialized',
        extraData: { fingerprint: ['layout-worker-never-initialized'] },
      };
    case 'abandoned':
      return {
        error: Error(
          'Layout worker did not signal that it was initialized and was abandoned'
        ),
        message:
          'Layout worker did not signal that it was initialized and was abandoned',
        extraData: { fingerprint: ['layout-worker-abandoned'] },
      };
    case 'error': {
      const { error, message, filename, lineno, colno } =
        cause.event as ErrorEvent;
      return {
        error: error ?? Error(message || 'The browser gave no details'),
        message:
          'The layout worker failed to load or threw before it could report',
        extraData: {
          isErrorEvent: cause.event instanceof ErrorEvent,
          filename,
          lineno,
          colno,
          fingerprint: ['layout-worker-error'],
        },
      };
    }
    case 'construction':
      return {
        error:
          cause.error instanceof Error
            ? cause.error
            : Error(String(cause.error)),
        message: 'The layout worker could not be constructed',
        extraData: { fingerprint: ['layout-worker-construction-failed'] },
      };
  }
};

export const reportLayoutWorkerStartFailure = (
  failure: LayoutWorkerStartFailure,
  { requestedUrl, viewId }: LayoutRequestContext
): void => {
  const { waitedMs, wallClockMs, wasHidden, attempt, next } = failure;
  const { error, message, extraData } = describeCause(failure);
  const fellBackToMainThread = next === 'mainThread';
  const log = fellBackToMainThread ? logError : logWarn;
  log(error, message, {
    ...extraData,
    fingerprint: fellBackToMainThread
      ? [...extraData.fingerprint, 'fell-back-to-main-thread']
      : extraData.fingerprint,
    waitedMs,
    wallClockMs,
    wasHidden,
    visibilityState: document.visibilityState,
    online: navigator.onLine,
    msSincePageLoad: Math.round(performance.now()),
    attempt,
    next,
    requestedUrl,
    viewId,
  });
};

export const reportLateLayoutWorkerStart = (
  { waitedMs, wallClockMs, wasHidden, attempt }: LateLayoutWorkerStart,
  { requestedUrl, viewId }: LayoutRequestContext
): void => {
  logWarn(
    Error('Layout worker signalled initialized after the start deadline'),
    'Layout worker signalled initialized after the start deadline',
    {
      waitedMs,
      wallClockMs,
      wasHidden,
      visibilityState: document.visibilityState,
      online: navigator.onLine,
      msSincePageLoad: Math.round(performance.now()),
      attempt,
      requestedUrl,
      viewId,
      fingerprint: ['layout-worker-initialized-late'],
    }
  );
};

export const attachLayoutWorkerErrorReporting = (worker: Worker): void => {
  worker.addEventListener('message', event => {
    if (isWorkerError(event.data)) {
      logWorkerError(event.data);
    }
  });
  worker.addEventListener('messageerror', () => {
    logError(
      Error('Layout worker sent a message that could not be deserialized'),
      'Layout worker sent a message that could not be deserialized',
      { fingerprint: ['layout-worker-messageerror'] }
    );
  });
};
